import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation } from 'react-router';
import LinkedTestCasesComponent from './linked-test-cases/LinkedTestCasesComponent';
import TestCycleDetails from './single-test-cycle/TestCycleDetails';
import TestCycleLoggedIssues from './TestCycleLoggedIssues';
import TestCycleHistory from './history/TestCycleHistory';
import { singleTestCyclePageTabs } from '../test-case/testCaseTableHead';
import Loader from '../../elements/Loader';
import Attachments from '../../elements/common/attachments/Attachments';
import Comments from '../../elements/common/comments/Comments';
import SecondaryHeader from '../../elements/SecondaryHeader';
import TestCycleReport from '../../elements/common/reports/TestCycleReport';
import Label from '../../elements/Label';
import { fetchTestCycle } from '../../../modules/testcycles';
import { useNotification } from '../../../hooks/NotificationContext';
import { formatDate } from '../../../lib/utils';
import TestPlayerPageComponent from '../test-execution/TestPlayerPage';
import Error from '../../elements/Error';
import { Link } from 'react-router-dom';
import { useProjectUsers } from '../../../query/apiQueries';

const entityType = 'PluginTestCycle';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const TestCycleViewPage = ({ testCycleKey, projectId }) => {
  const query = useQuery();
  const searchTerm = query.get('type');
  const [activeTab, setActiveTab] = useState(searchTerm || 'executions');
  const [loading, setLoading] = useState();
  const [data, setData] = useState(null);
  const { addNotification } = useNotification();
  const [error, setError] = useState(false);

  const fetchTestCycleData = async () => {
    setLoading(true);
    try {
      const response = await fetchTestCycle(testCycleKey, projectId);
      setData(response);
    } catch (error) {
      if (error?.message === "NOT_FOUND") {
        setError(true);
      } else {
        const errorMessage =
          error?.message ||
          "Unable to fetch test cycle. Please try again...";
        addNotification("danger", errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const { data: users = [] } = useProjectUsers(projectId);

  useEffect(() => {
    if (!testCycleKey || !projectId) return;
    fetchTestCycleData();
  }, [testCycleKey, projectId]);

  if (error) {
    return (
      <Error>
        <h2>Test Cycle Not Found</h2>
        <p className="text-center">
          The test cycle you're looking for doesn't exist or may have been removed.{" "}
           <Link to={`/project/${projectId}/testcycles`}>Click here{" "}</Link>to view all the test cycles
        </p>
      </Error>
    );
  }

  return (
    <>
      {loading || !data ? (
        <Loader />
      ) : (
        <Container fluid>
          <SecondaryHeader
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            pageTitle={
              <>
                {data?.name}
                <span style={{ marginLeft: '10px' }}>
                  <Label
                    color={data?.status?.color}
                    name={data?.status?.name}
                  />
                </span>
              </>
            }
            breadcrumbs={[
              {
                label: 'Testcycles',
                path: `/project/${projectId}/testcycles`,
                clickable: true,
              },
              {
                label: data?.key,
                path: `/project/${projectId}/testcycles/${testCycleKey}`,
                clickable: false,
              },
            ]}
            tabHeaders={singleTestCyclePageTabs}
            otherDetails={
              <>
              <p className="ellipsis mb-0 pb-2" style={{ lineHeight: '20px' }}>
                <span style={{ fontSize: '16px' }}>
                  {data?.platforms && data?.platforms.length > 0 ? `${data?.platforms[0]}` : ''} {" "}
                  {data?.sub_platforms && data?.sub_platforms.length > 0 ? ` | ${data?.sub_platforms[0]}` : ''} {" "}
                  {data?.browsers && data?.browsers.length > 0 ? `| ${data?.browsers[0]}` : ''}
                </span></p>
                <p>Created On: {formatDate(data?.created_at)}</p>
                </>
            }
            showBackButton={true}
          />

          <Container fluid className="px-0">
            {activeTab === 'details' && (
              <TestCycleDetails
                entityId={data?.id}
                data={data && data}
                fetchTestCycleData={fetchTestCycleData}
              />
            )}
             {activeTab === 'executions' && (
              <TestPlayerPageComponent 
                testCycleId={data?.id} 
                projectId={projectId} 
                testCycleData={data && data} 
              />
            )}
            {activeTab === 'testcases' && (
              <LinkedTestCasesComponent
                entityId={data?.id}
                users={users}
                data={data}
                fetchTestCycleData={fetchTestCycleData}
              />
            )}
            {activeTab === 'traceability' && (
              <TestCycleLoggedIssues entityId={data?.id} testcycle={data && data} />
            )}
            {activeTab === 'attachments' && (
              <Container fluid className="px-0">
                <Attachments
                  entityType={entityType}
                  users={users}
                  entityId={data?.id}
                />
              </Container>
            )}
            {activeTab === 'comments' && (
              <Container fluid className="px-0">
                <Comments
                  entityType={entityType}
                  entityId={data?.id}
                  users={users}
                  entityKey={data?.key}
                />
              </Container>
            )}
            {activeTab === 'reports' && (
              <Container fluid className="px-0">
                <TestCycleReport
                  testCycleId={data?.id}
                  projectId={projectId}
                  testCycleData={data}
                />
              </Container>
            )}
            {activeTab === 'history' && (
              <Container fluid className="px-0">
                <TestCycleHistory
                  entityType={entityType}
                  entityId={data?.id}
                  users={users}
                />
              </Container>
            )}
          </Container>
        </Container>
      )}
    </>
  );
};

export default TestCycleViewPage;
